import * as React from 'react'
import { Link, createFileRoute, redirect } from '@tanstack/react-router'
import { AlertTriangle, ArrowLeft, RefreshCw } from 'lucide-react'
import { z } from 'zod'
import { SectionHeading } from '../../-components/SectionHeading'
import { AnalyticsRangeControl } from '../../-components/AnalyticsRangeControl'
import { EpCountButton } from '../../-components/EpCountButton'
import { KpiRecordsDialog } from '../../-components/KpiRecordsDialog'
import {
  EP_OUTCOME_LABEL,
  EP_OUTCOME_TONE,
  epOutcomePersonDrillTitle,
} from '../../-components/ep-outcome-display'
import type {
  AnalyticsFilters,
  AnalyticsTab,
  EpOutcomeFilter,
} from '@/types/backofficeAnalytics.types'
import type { RollupExperienceType } from '@/components/shared/ExperienceRollupCard'
import type { Department as DepartmentType } from '@/types/user.types'
import type { AnalyticsRangePreset } from '@/lib/analytics-range-presets'
import { ANALYTICS_TABS } from '@/types/backofficeAnalytics.types'
import { departmentLabel } from '@/types/department'
import { Department } from '@/types/user.types'
import { PageTitle } from '@/components/PageTitle'
import { UserAvatar } from '@/components/UserAvatar'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card, CardContent } from '@/components/ui/card'
import { Skeleton } from '@/components/ui/skeleton'
import { StatTile } from '@/components/shared/StatTile'
import { InfoHint } from '@/components/shared/InfoHint'
import { inferPreset, resolvePreset } from '@/lib/analytics-range-presets'
import {
  formatDateOnlyShort,
  formatDurationMs,
  formatPendingEpCaption,
  formatSampleCaption,
} from '@/lib/formatters'
import { parseCsvParam } from '@/lib/csv-search-param'
import {
  useBackofficeAnalyticsPerson,
  useBackofficeAnalyticsSpecialistPeople,
} from '@/hooks/repositories/useBackofficeAnalyticsRepository'

const personSearchSchema = z.object({
  range: z
    .enum(['30d', '90d', '365d', 'month', 'custom'])
    .optional()
    .catch(undefined),
  from: z.string().optional().catch(undefined),
  to: z.string().optional().catch(undefined),
  /** `YYYY-MM` for `range=month`. */
  month: z.string().optional().catch(undefined),
  preset: z
    .enum([
      '7d',
      '30d',
      '90d',
      'thisMonth',
      'lastMonth',
      'qtd',
      'ytd',
      '365d',
      'custom',
    ])
    .optional()
    .catch(undefined),
  experience: z.string().optional().catch(undefined),
  department: z.string().optional().catch(undefined),
  /**
   * Which analytics tab this page was opened from (one of
   * `ANALYTICS_DEPARTMENT_TABS`'s values) — carried by whichever row's Link
   * sent the reader here, so the back link returns them to where they
   * actually came from. Absent on a deep link or a cold refresh, where
   * `planners` is the default: the first department tab, which is where the
   * console itself lands for a caller who cannot see Overview. A stale
   * bookmark naming a removed tab (`people`, `advisors`, `directors`) fails
   * the enum and is treated as absent.
   */
  tab: z.enum(ANALYTICS_TABS).optional().catch(undefined),
})

export const Route = createFileRoute(
  '/_app/backoffice/analytics/users/$userId/',
)({
  component: RouteComponent,
  validateSearch: personSearchSchema,
  beforeLoad: ({ context }) => {
    const { isAuthenticated, isLoading, departmentResolved, user } =
      context.auth
    if (!isAuthenticated && !isLoading) {
      throw redirect({ to: '/login' })
    }
    // Only redirect once the department is actually resolved and known to be
    // non-admin. While it's still loading (or the fetch errored) we stay put
    // and let the server-side @RequireAdmin guard enforce access — otherwise a
    // cold refresh/deep-link bounces admins to /home before department loads.
    if (departmentResolved && user && user.department !== Department.ADMIN) {
      throw redirect({ to: '/home' })
    }
  },
})

function RouteComponent() {
  const { userId } = Route.useParams()
  const search = Route.useSearch()
  const navigate = Route.useNavigate()

  const filters: AnalyticsFilters = {
    range: search.range ?? '30d',
    from: search.from,
    to: search.to,
    month: search.month,
    experience: parseCsvParam(search.experience) as Array<RollupExperienceType>,
    department: parseCsvParam(search.department) as Array<DepartmentType>,
    // This page is already scoped to one person by the `$userId` path param —
    // the `person` filter exists to narrow a roster/KPI-card view to a subset
    // of people, which doesn't apply here.
    person: [],
  }
  const preset = inferPreset({ range: filters.range, preset: search.preset })

  const {
    data: person,
    isPending,
    isError,
    refetch,
  } = useBackofficeAnalyticsPerson(userId, filters)

  // The three specialist-throughput figures (sc-1212 follow-up) — the same
  // `/analytics/specialist-people` endpoint the Specialists tab's table
  // reads, scoped to this one person via `person: [userId]` rather than a
  // dedicated per-person endpoint for three numbers already served by this
  // one. Works for any staff member, not just specialists — the endpoint
  // returns every active user the caller can see, zeroed/null for anyone
  // who hasn't done this kind of work.
  const specialistPeopleFilters: AnalyticsFilters = {
    ...filters,
    person: [userId],
  }
  const {
    data: specialistPeople,
    isPending: isSpecialistPending,
    // `isLoadingError`, not `isError`: this query polls (sc-1620), and a
    // failed poll must not replace figures that are still cached with
    // "Couldn't load" — see `HUBSPOT_REPORT_POLLING`.
    isLoadingError: isSpecialistError,
    refetch: refetchSpecialist,
  } = useBackofficeAnalyticsSpecialistPeople(specialistPeopleFilters, {
    enabled: Boolean(userId),
  })
  const specialistRow = specialistPeople?.rows.find(
    (row) => row.userId === userId,
  )
  // Drives both the clickable tiles' aria-labels and the dialog's title —
  // from `specialistRow` rather than `person` (the profile query above) so
  // it's defined at exactly the moment the tiles themselves render (both
  // come from the same `specialistPeople` fetch).
  const personName = specialistRow
    ? `${specialistRow.firstName} ${specialistRow.lastName}`.trim()
    : ''

  // Which Plan Delivery `StatTile` was clicked, doubling as `KpiRecordsDialog`'s
  // open state — the same drill-in `SpecialistPeopleTab`'s table wires up
  // (sc-1212, Tyler 2026-09-30: "i cant drill down into Garrets 44 late ones
  // and investigate why"). This page is already scoped to one person, so
  // `specialistPeopleFilters` (already `person: [userId]`) is exactly the
  // filters the dialog needs — no per-click override required.
  const [epDrill, setEpDrill] = React.useState<EpOutcomeFilter | null>(null)

  // Used to have been display-only ("inherits `range` from the back link,
  // silently defaults to 30d, offers no UI"). A person's numbers are
  // unattributable without a window, so this is a real control, not a pill —
  // one navigate per change, matching `useAnalyticsFilters.setRangePreset`.
  const setRangePreset = (nextPreset: AnalyticsRangePreset) => {
    const resolved = resolvePreset(nextPreset)
    void navigate({
      search: (prev) => ({
        ...prev,
        range: resolved.range,
        from: resolved.from,
        to: resolved.to,
        month: resolved.month,
        preset: nextPreset,
      }),
      replace: true,
    })
  }
  const setCustomRange = (bounds: {
    from: string | null
    to: string | null
  }) => {
    void navigate({
      search: (prev) => ({
        ...prev,
        range: 'custom',
        from: bounds.from ?? undefined,
        to: bounds.to ?? undefined,
        month: undefined,
        preset: 'custom',
      }),
      replace: true,
    })
  }

  const rangeControl = (
    <AnalyticsRangeControl
      id="person-analytics-range"
      ariaLabel="Range"
      preset={preset}
      from={filters.from}
      to={filters.to}
      onPresetChange={setRangePreset}
      onCustomRangeChange={setCustomRange}
      triggerClassName="sm:w-40"
    />
  )

  // Returns to wherever the row that opened this page actually lived —
  // `planners` is the default for a deep link or a cold refresh, where no
  // `tab` ever reached the URL.
  const originTab: AnalyticsTab = search.tab ?? 'planners'
  const backLink = (
    <Button variant="ghost" size="sm" asChild className="-ml-2 w-fit">
      <Link to="/backoffice/analytics" search={{ ...search, tab: originTab }}>
        <ArrowLeft className="mr-1 size-4" aria-hidden />
        Back to analytics
      </Link>
    </Button>
  )

  if (isError) {
    return (
      <div className="space-y-6 py-4">
        <PageTitle title="Internal Reporting and KPIs" />
        <div className="flex flex-wrap items-center justify-between gap-2">
          {backLink}
          {/* The figures below are all scoped to this window, inherited from the
            back link. Without saying so the page presents range-scoped numbers
            as if they were lifetime totals. */}
          {rangeControl}
        </div>
        <Card>
          <CardContent className="py-10 text-center">
            <p className="text-sm font-medium">Couldn’t load this person</p>
            <p className="mt-1 text-sm text-muted-foreground">
              They may have been deactivated, or the request failed.
            </p>
            <Button
              size="sm"
              variant="outline"
              className="mt-3"
              onClick={() => void refetch()}
            >
              Retry
            </Button>
          </CardContent>
        </Card>
      </div>
    )
  }

  return (
    <div className="space-y-6 py-4">
      <PageTitle
        title={
          person
            ? `${person.firstName} ${person.lastName}`
            : 'Internal Reporting and KPIs'
        }
      />
      <div className="flex flex-wrap items-center justify-between gap-2">
        {backLink}
        <div className="flex flex-wrap items-center gap-3">
          {/* The window this page's own data was resolved against, not just
              the request that was sent — the server may cap `to` at "now". */}
          {person && (
            <p className="text-xs text-muted-foreground tabular-nums">
              {formatDateOnlyShort(person.window.from)} —{' '}
              {formatDateOnlyShort(person.window.to)}
            </p>
          )}
          {rangeControl}
        </div>
      </div>

      <Card>
        <CardContent className="flex flex-col gap-4 pt-4 sm:flex-row sm:items-center">
          {isPending ? (
            <>
              <Skeleton className="size-16 rounded-full" />
              <div className="space-y-2">
                <Skeleton className="h-6 w-48" />
                <Skeleton className="h-4 w-32" />
              </div>
            </>
          ) : (
            <>
              <UserAvatar user={person} size="lg" className="size-16" />
              <div className="min-w-0">
                <h1 className="truncate text-xl font-bold tracking-tight sm:text-2xl">
                  {person.firstName} {person.lastName}
                </h1>
                <p className="truncate text-sm text-muted-foreground">
                  {person.title ?? person.email}
                </p>
                <Badge variant="outline" className="mt-1.5 text-xs">
                  {departmentLabel(person.department)}
                </Badge>
              </div>
            </>
          )}
        </CardContent>
      </Card>

      <section aria-labelledby="person-plan-work-heading" className="space-y-3">
        <SectionHeading id="person-plan-work-heading">
          Specialist throughput
        </SectionHeading>
        {isSpecialistError ? (
          <Card>
            <CardContent className="py-10 text-center">
              <AlertTriangle
                className="mx-auto mb-2 size-5 text-muted-foreground"
                aria-hidden
              />
              <p className="text-sm text-muted-foreground">
                Couldn’t load these figures.
              </p>
              <Button
                size="sm"
                variant="outline"
                className="mt-3"
                onClick={() => void refetchSpecialist()}
              >
                <RefreshCw className="mr-1.5 size-3.5" aria-hidden />
                Retry
              </Button>
            </CardContent>
          </Card>
        ) : (
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
            <StatTile
              label="Forecasts created"
              value={specialistRow?.forecastsCreated ?? 0}
              isLoading={isSpecialistPending}
            />
            <StatTile
              label="Escape plans generated"
              value={specialistRow?.escapePlansGenerated ?? 0}
              // Attributed to whoever ACTIVATED the escape plan, not whoever
              // created the forecast above — frequently a different person,
              // never a subset of it.
              caption="Activated, not a subset of forecasts created"
              isLoading={isSpecialistPending}
            />
            <StatTile
              label={
                <span className="inline-flex items-center gap-1">
                  First pass
                  <InfoHint label="What First pass measures">
                    Median Plan Work time spent BUILDING a plan, before anyone
                    asked for a change. Credited to the ticket owner. Windowed
                    on the arrival at Ready for Presentation. Only cases that
                    reached the build phase AND have finished it count — a case
                    still being built is withheld and shown separately as
                    &ldquo;in flight&rdquo;.
                    <br />
                    <br />
                    <strong>Not comparable between people.</strong> This
                    measures how long a person LEAVES a ticket in this stage,
                    not how fast they build a plan — use it against this
                    person&rsquo;s own history over time, never to rank them
                    against a peer.
                  </InfoHint>
                </span>
              }
              value={formatDurationMs(specialistRow?.firstPassMedianMs ?? null)}
              caption={
                <>
                  {specialistRow && specialistRow.firstPassMedianMs !== null
                    ? formatSampleCaption(specialistRow.firstPassSampleSize)
                    : 'No cases reached the build phase in this window'}
                  {specialistRow && specialistRow.firstPassInFlight > 0 && (
                    <span className="block">
                      +{specialistRow.firstPassInFlight.toLocaleString()} in
                      flight
                      {specialistRow.firstPassInFlightOldestMs !== null &&
                        ` · oldest ${formatDurationMs(specialistRow.firstPassInFlightOldestMs)}`}
                    </span>
                  )}
                </>
              }
              isLoading={isSpecialistPending}
            />
            <StatTile
              label={
                <span className="inline-flex items-center gap-1">
                  Rework
                  <InfoHint label="What Rework measures">
                    Median Plan Work time spent servicing a CHANGE REQUEST —
                    Alternative Scenarios Needed or Revisions Needed — on a plan
                    already delivered. Its own sample, not a subset of First
                    pass&rsquo;s: a case that took no change request contributes
                    to First pass and not to this. A person who took zero change
                    requests in the window shows &ldquo;None&rdquo; here, a real
                    good outcome, not a missing measurement. A case still
                    servicing a change request right now is withheld (its
                    duration is still climbing) and shown separately as
                    &ldquo;in flight&rdquo;.
                  </InfoHint>
                </span>
              }
              value={
                specialistRow && specialistRow.reworkMedianMs === null
                  ? 'None'
                  : formatDurationMs(specialistRow?.reworkMedianMs ?? null)
              }
              caption={
                <>
                  {specialistRow && specialistRow.reworkMedianMs !== null
                    ? formatSampleCaption(
                        specialistRow.reworkSampleSize,
                        'took at least one change request',
                      )
                    : 'No change requests in this window'}
                  {specialistRow && specialistRow.reworkInFlight > 0 && (
                    <span className="block">
                      +{specialistRow.reworkInFlight.toLocaleString()} in flight
                      {specialistRow.reworkInFlightOldestMs !== null &&
                        ` · oldest ${formatDurationMs(specialistRow.reworkInFlightOldestMs)}`}
                    </span>
                  )}
                </>
              }
              isLoading={isSpecialistPending}
            />
            <StatTile
              label={
                <span className="inline-flex items-center gap-1">
                  Plan Work (combined)
                  <InfoHint label="What the combined Plan Work figure is">
                    Median total plan work per case — NOT a sum of First pass +
                    Rework above. It is a median over every case that finished
                    plan work, while Rework is a median over the much smaller
                    subset that took a change request, so this can read LOWER
                    than Rework alone: most cases never take a change request at
                    all. See First pass and Rework above for the two halves
                    split apart.
                  </InfoHint>
                </span>
              }
              value={formatDurationMs(specialistRow?.planWorkMedianMs ?? null)}
              caption={
                specialistRow && specialistRow.planWorkMedianMs !== null
                  ? formatSampleCaption(specialistRow.planWorkSampleSize)
                  : 'No cases reached the plan phase in this window'
              }
              isLoading={isSpecialistPending}
            />
            <StatTile
              label={
                <span className="inline-flex items-center gap-1">
                  Financial Planning
                  <InfoHint label="What Financial Planning counts">
                    Cases reaching Ready in this window whose service type is
                    Financial Planning (Integration Experience + Financial
                    Planning tokens together) rather than Rothology-only. The
                    caption is the TOTAL Plan Work time spent on those cases,
                    not a median — the question is who is carrying more of this
                    ongoing work, and a per-person median would be undefined for
                    almost everyone. Annual Review and unclassified cases count
                    toward neither this nor Rothology.
                  </InfoHint>
                </span>
              }
              value={specialistRow?.financialPlanningCases ?? 0}
              caption={
                specialistRow
                  ? `${formatDurationMs(specialistRow.financialPlanningPlanWorkMs)} plan work`
                  : undefined
              }
              isLoading={isSpecialistPending}
            />
            <StatTile
              label={
                <span className="inline-flex items-center gap-1">
                  {EP_OUTCOME_LABEL.onTime}
                  <InfoHint label="What counts as on time">
                    Ready for Presentation was reached one business week or more
                    before the client&rsquo;s first delivery meeting. Business
                    days are Monday&ndash;Friday; no holiday calendar is
                    applied. Windowed on the MEETING date, not the delivery
                    date.
                  </InfoHint>
                </span>
              }
              value={
                specialistRow ? (
                  <EpCountButton
                    count={specialistRow.onTimeEp}
                    onClick={() => setEpDrill('onTime')}
                    ariaLabel={`View ${personName}'s ${specialistRow.onTimeEp} on-time plan deliveries`}
                  >
                    {specialistRow.onTimeEp}
                  </EpCountButton>
                ) : (
                  0
                )
              }
              tone={specialistRow?.onTimeEp ? EP_OUTCOME_TONE.onTime : 'idle'}
              caption={
                specialistRow && specialistRow.pendingEp > 0
                  ? formatPendingEpCaption(specialistRow.pendingEp)
                  : undefined
              }
              isLoading={isSpecialistPending}
            />
            <StatTile
              label={
                <span className="inline-flex items-center gap-1">
                  {EP_OUTCOME_LABEL.late}
                  <InfoHint label="What counts as late">
                    A plan existed, but reached Ready for Presentation with less
                    than one business week to spare before the client&rsquo;s
                    first delivery meeting — a process miss, not the same as a
                    meeting with nothing ready.
                  </InfoHint>
                </span>
              }
              value={
                specialistRow ? (
                  <EpCountButton
                    count={specialistRow.lateEp}
                    onClick={() => setEpDrill('late')}
                    ariaLabel={`View ${personName}'s ${specialistRow.lateEp} late plan deliveries`}
                  >
                    {specialistRow.lateEp}
                  </EpCountButton>
                ) : (
                  0
                )
              }
              // Passed unconditionally, as StatTile's own docs ask: `idle`
              // renders no tint. Zero here is the good outcome and must not
              // be dressed as an alert (§8).
              tone={specialistRow?.lateEp ? EP_OUTCOME_TONE.late : 'idle'}
              caption="Ready, but under a business week of lead"
              isLoading={isSpecialistPending}
            />
            <StatTile
              label={
                <span className="inline-flex items-center gap-1">
                  {EP_OUTCOME_LABEL.missed}
                  <InfoHint label="What counts as missed">
                    The client&rsquo;s first delivery meeting happened with no
                    plan ready at all — the real failure, distinct from Late.
                  </InfoHint>
                </span>
              }
              value={
                specialistRow ? (
                  <EpCountButton
                    count={specialistRow.missedEp}
                    onClick={() => setEpDrill('missed')}
                    ariaLabel={`View ${personName}'s ${specialistRow.missedEp} missed plan deliveries`}
                  >
                    {specialistRow.missedEp}
                  </EpCountButton>
                ) : (
                  0
                )
              }
              tone={specialistRow?.missedEp ? EP_OUTCOME_TONE.missed : 'idle'}
              caption="Meeting happened, no plan ready"
              isLoading={isSpecialistPending}
            />
            <StatTile
              label={
                <span className="inline-flex items-center gap-1">
                  {EP_OUTCOME_LABEL.lateApproval}
                  <InfoHint label="What counts as a late approval">
                    The data-gathering form wasn&rsquo;t approved until the day
                    of the client&rsquo;s presentation or later, so the Case
                    Design ticket didn&rsquo;t exist for anyone to build
                    against. A PLANNER&rsquo;s miss, not this person&rsquo;s —
                    shown on this row because this page has no planner row to
                    carry it. Never added to Late or Missed above.
                  </InfoHint>
                </span>
              }
              value={
                specialistRow ? (
                  <EpCountButton
                    count={specialistRow.lateApprovalEp}
                    onClick={() => setEpDrill('lateApproval')}
                    // Never "<name>'s late approvals" — this is a PLANNER's
                    // miss reported on the specialist's row, not their own
                    // failure. See `epOutcomePersonDrillTitle`'s doc comment.
                    ariaLabel={`View the ${specialistRow.lateApprovalEp} case${specialistRow.lateApprovalEp === 1 ? '' : 's'} on ${personName}'s row with a late form approval`}
                  >
                    {specialistRow.lateApprovalEp}
                  </EpCountButton>
                ) : (
                  0
                )
              }
              // `info`, never `error`/`action` — a planner's miss reported
              // on the specialist's row, not a grade on their own work. See
              // `EP_OUTCOME_TONE.lateApproval`'s doc comment.
              tone={
                specialistRow?.lateApprovalEp
                  ? EP_OUTCOME_TONE.lateApproval
                  : 'idle'
              }
              caption="Form approved too late — a planner miss"
              isLoading={isSpecialistPending}
            />
          </div>
        )}
      </section>

      <KpiRecordsDialog
        open={epDrill !== null}
        onOpenChange={(open) => {
          if (!open) setEpDrill(null)
        }}
        metric="epOutcomes"
        metricTitle={
          epDrill ? epOutcomePersonDrillTitle(personName, epDrill) : ''
        }
        filters={specialistPeopleFilters}
        outcome={epDrill ?? undefined}
      />
    </div>
  )
}
